/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs them, and displays a fallback UI instead of crashing the app.
 *
 * IMPORTANT: This is a safety-critical application. Error boundaries
 * ensure users can still access official emergency links even if
 * the app encounters an error.
 */

import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({ errorInfo });

    // In production, you would send this to an error tracking service
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <h1>Something went wrong</h1>
            <p>
              The application encountered an unexpected error. Please try refreshing the page.
            </p>

            <div className="error-boundary-actions">
              <button onClick={this.handleReload} className="error-boundary-btn primary">
                Refresh Page
              </button>
            </div>

            <div className="error-boundary-emergency">
              <h2>Emergency Resources</h2>
              <p>For official bushfire information, please use these sources:</p>
              <ul>
                <li>
                  <a href="tel:000" className="emergency-link">
                    Triple Zero (000) - Emergency calls only
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.rfs.nsw.gov.au/fire-information/fires-near-me"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    NSW RFS - Fires Near Me
                  </a>
                </li>
                <li>
                  <a href="https://www.hazardsnearme.nsw.gov.au/" target="_blank" rel="noopener noreferrer">
                    Hazards Near Me App
                  </a>
                </li>
                <li>
                  <a href="tel:1800679737">Bush Fire Information Line: 1800 679 737</a>
                </li>
              </ul>
            </div>

            {/* Show error details in development */}
            {import.meta.env.DEV && this.state.error && (
              <details className="error-boundary-details">
                <summary>Error Details (Development Only)</summary>
                <pre>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
